import React, { useContext, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import BigModal from '../../../../../components/Modals/BigModal'
import AnDateField from '../../../../../components/DateTime/DateField'
import API from '../../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'

const categories = ['Venue', 'Equipment', 'Logistics', 'Subsistence', 'Other']

const AddEventExpense = ({ open, onClose, eventID, onAdded }) => {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('Other')
  const [amount, setAmount] = useState('')
  const [expenseDate, setExpenseDate] = useState(moment())
  const [receiptFile, setReceiptFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'

  const reset = () => {
    setDescription('')
    setCategory('Other')
    setAmount('')
    setExpenseDate(moment())
    setReceiptFile(null)
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      let receipt
      if (receiptFile) {
        const uploadUrlRes = await API.post('/gets3uploadurl', {
          folder: 'events/expenses',
          extension: receiptFile.type.split('/')[1],
        })
        await API.put(uploadUrlRes.data.url, receiptFile, {
          headers: { 'Content-Type': receiptFile.type },
        })
        receipt = {
          key: uploadUrlRes.data.key,
          name: receiptFile.name,
          contentType: receiptFile.type,
          size: receiptFile.size,
        }
      }

      const res = await API.post(`/events/${eventID}/expenses`, {
        description,
        category,
        amount: parseFloat(amount),
        expenseDate,
        receipt,
      })

      if (res.status === 200) {
        setToast({ message: 'Expense added' })
        reset()
        onClose()
        onAdded?.()
      } else {
        setError(res?.data?.error || 'Unable to add this expense.')
      }
    } catch (e) {
      setError('Unable to add this expense. Please try again later.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <BigModal open={open} onClose={onClose}>
      <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
        <Grid item xs={12}>
          <Typography variant="title2">Add Event Expense</Typography>
        </Grid>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        <Grid item xs={12} lg={4}>
          <AnDateField
            date={expenseDate}
            onChange={(value) => setExpenseDate(value)}
            label="Date"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="Category"
            fullWidth
          >
            {categories.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            label="Amount"
            fullWidth
            required
            InputProps={{
              inputProps: { min: 0, step: 0.01 },
              startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            label="Description"
            fullWidth
            required
            multiline
            minRows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            type="file"
            label="Receipt (optional)"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: 'application/pdf,image/*' }}
            onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="success"
            fullWidth
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Submit'}
          </Button>
        </Grid>
      </Grid>
    </BigModal>
  )
}

export default AddEventExpense
